import React, { useMemo } from "react";
import type { Project } from "../../../../../../../../../api/useProjects";
import type { Source } from "../../../../../../../../../api/useSources";
import type { ActiveAnalysis } from "../../../../../../../../../api/useActiveAnalysesStream";
import { type Quantity } from "../../../../../../../../../api/useQuantities";
import t from "../../../../../../../../../i18n";
import { css, cx } from "@linaria/core";
import { FullViewportTrigger, FullViewportButtonMarker } from "@ui/FullViewportButton";
import InfoTooltip from "../../../../../../../Chart/InfoTooltip";
import Selector from "@ui/Selector";
import PeriodMenuDiagnosis from "../../../../../../../Diagnosis/FailureEvolution/components/PeriodMenuDiagnosis";
import { usePublicationFlagDisplay } from "../../../../../../../Analysis/TogglePublicationDisplay";
import { useSettings } from "../../../../../../../../../api/useSettings";
import ActionMenu from "../../../../../../../Analysis/AnalysisActionMenu";
import { useCanUserDoOnProject } from "../../../../../../../../canUserDo";
import ChartCard from "@ui/Highcharts/ui/Card";
import type { Setup } from "../../../../../../../../../api/useSetups";
import type { IndexedFailureValues } from "../../../../../../../../../api/useIndexedFailureValues";
import type { IndexedManualFailureValues } from "../../../../../../../../../api/useManualFailureValues";
import { fonts } from "@ui";
import { colors, spacings } from "@ui/Theme";
import { ExportWidgetButton, exportWidgetTarget } from "@ui/ExportWidgetButton";
import type { IndexedFailureMetadata } from "../../../../../../../../../api/useFailureMetadata";
import {
  FAULT_AMPLITUDE_CHART,
  FAULT_IMPACT_CHART,
  useFaultGraphOptions,
} from "./hook/useFaultGraphOptions";
import Text from "@ui/Text";
import { GitCommitLine, InboxArchiveLine, InformationLine, RefreshLine } from "@ui/IconV2/react";
import Select from "@ui/Select";
import Button from "@ui/Button";
import PeriodSelector from "../../../../FaultEvent/components/PeriodSelector";
import Badge from "@ui/Badge";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import TooltipsV2 from "@ui/TooltipV2/index";
import { getDetectionTag } from "../../../../../../utils/faultEvent";
import { generateExportFileName } from "diagnostics-lib/string";
import type { Event } from "../../../../../../../../../api/useEvents";
import { useEventPeriod } from "../../../../../../hooks/useEventPeriod";
import useActiveAnalysesWithDisplayFilters from "../../../../../../../../../api/useActiveAnalysesWithDisplayFilters";
import DiagnosisHighcharts from "./DiagnosisHighcharts";
import { useDisplayAllFaults } from "../useDisplayAllFaults";
import EvolutionSubHeader from "../../../../../../../EvolutionSubHeader";
import { DisplayFiltersBlankState } from "../../../../../../../EvolutionSubHeader/DisplayFiltersBlankState";

export const EXCLUDE_FULLSCREEN = "exclude-fullscreen";

const FailureEvolution = ({
  eventScope,
  event,
  project,
  quantities,
  setups,
  source,
  activeAnalyses,
  indexedFailureMetadata,
  indexedFailureValues,
  indexedManualFailureValues,
  onCloseEvent,
}: {
  eventScope: string;
  event: Event;
  project: Project;
  quantities: Quantity[];
  activeAnalyses: ActiveAnalysis[];
  setups: Setup[];
  source: Source;
  indexedFailureMetadata: IndexedFailureMetadata;
  indexedFailureValues: IndexedFailureValues;
  indexedManualFailureValues: IndexedManualFailureValues;
  onCloseEvent?: () => void;
}) => {
  const settings = useSettings();

  const { displayFilterRemovesAllActiveAnalyses } = useActiveAnalysesWithDisplayFilters(project.id);

  const canUserWriteProject = useCanUserDoOnProject("PROJECT_WRITE", project.id);
  const canUserReadProject = useCanUserDoOnProject("PROJECT_READ", project.id);

  const [publicationFlagDisplayed] = usePublicationFlagDisplay({
    scope: project.id,
    defaultValue: settings.DISPLAY_PUBLICATION_MARKERS,
  });

  const { graphOptionMenu, graphOptionSelector } = useFaultGraphOptions({
    event,
    quantities,
  });
  const [_displayAllFaults, setDisplayAllFaults] = useDisplayAllFaults({
    scope: eventScope,
  });

  const [_, __, ___, currentPeriodIsDefaultPeriod, setPeriodToDefault, ____] = useEventPeriod({
    event,
    source,
    project,
    analyses: activeAnalyses,
    boundaries: "active-analyses",
  });
  const onChangeChartMenu = React.useCallback(
    (value: string) => {
      if (graphOptionMenu.graphType === value) return;

      graphOptionMenu.setGraphType(value);
      if (value !== FAULT_AMPLITUDE_CHART) return;

      if (value === FAULT_AMPLITUDE_CHART) {
        setDisplayAllFaults(false);
      }
    },
    [setDisplayAllFaults, graphOptionMenu],
  );

  const leftOptions = React.useMemo(
    () => (
      <div className={leftOptionsContainer}>
        {event.type === "DIAGNOSED" && (
          <Select
            prompt=""
            options={graphOptionMenu.graphTypeOptions}
            value={graphOptionMenu.graphType}
            onChange={e => onChangeChartMenu(e.target.value)}
            clearable={false}
          />
        )}
        {graphOptionMenu.graphType === FAULT_IMPACT_CHART &&
        graphOptionSelector.impactsSelectorOptions.length > 1 ? (
          <div>
            <Selector
              value={graphOptionSelector.impactSelector}
              options={graphOptionSelector.impactsSelectorOptions}
              onChange={value => graphOptionSelector.setImpactSelector(value)}
            />
          </div>
        ) : null}
      </div>
    ),
    [graphOptionMenu, graphOptionSelector, onChangeChartMenu, event.type],
  );

  const detectionTag =
    event.type === "DIAGNOSED"
      ? getDetectionTag({
          isClosable: event.metadata.isClosable,
          statusByAnalysis: event.metadata.faults.flatMap(fault => fault.statusByAnalysis),
        })
      : true;

  const subtitleC = React.useMemo(
    () => (
      <EvolutionSubHeader
        projectIdToFilter={project.id}
        allAnalysesMode={false}
        chartOptions={leftOptions}
        periodSettings={
          <>
            {!currentPeriodIsDefaultPeriod && (
              <Button variant="tertiary" onClick={setPeriodToDefault} leftIcon={<RefreshLine />}>
                {t("project.fault_event.fault.chart.period_picker.reset")}
              </Button>
            )}
            <PeriodSelector
              event={event}
              project={project}
              source={source}
              analyses={activeAnalyses}
              boundaries="active-analyses"
            />
          </>
        }
        visibilitySettings={
          <PeriodMenuDiagnosis
            project={project}
            activeAnalyses={activeAnalyses}
            publicationFlagDisplayed={publicationFlagDisplayed}
          />
        }
        actionMenu={
          !displayFilterRemovesAllActiveAnalyses && (
            <ActionMenu
              event={event}
              analyses={activeAnalyses}
              project={project}
              canWriteProject={canUserWriteProject}
              canUserReadProject={canUserReadProject}
              allAnalysesMode={false}
            />
          )
        }
      />
    ),
    [
      activeAnalyses,
      canUserReadProject,
      canUserWriteProject,
      currentPeriodIsDefaultPeriod,
      event,
      leftOptions,
      displayFilterRemovesAllActiveAnalyses,
      project,
      publicationFlagDisplayed,
      setPeriodToDefault,
      source,
    ],
  );

  const titleChart = useMemo(
    () => (
      <div className={title}>
        <div className={flexSpacingXs}>
          <Text type="h4">{t("project.fault_event.chart.evolution.title")}</Text>
          <InfoTooltip
            title={t("diagnosis.overview.header.option.title")}
            options={graphOptionSelector.impactSelectorDescriptions}
            link="/faults-page"
            placement="top"
          >
            <div className={centerIcon}>
              <InformationLine size="s" color={colors.carbon800} />
            </div>
          </InfoTooltip>
          {event.metadata.status !== "CLOSED" &&
            (detectionTag === "singleEvent" ? (
              <TooltipsV2
                placement="top"
                overlay={
                  <OverlayWrapper
                    className={styledOverlayWrapper}
                    text={t("project.fault_event.badge.tooltips.single")}
                  />
                }
              >
                <div>
                  <Badge
                    color={colors.carbon900}
                    backgroundColor={colors.titanium100}
                    icon={<GitCommitLine color={colors.carbon800} size="s" />}
                  >
                    {t("project.fault_event.badge.tooltips.single")}
                  </Badge>
                </div>
              </TooltipsV2>
            ) : detectionTag === "closable" ? (
              <TooltipsV2
                placement="top"
                overlay={
                  <OverlayWrapper
                    className={styledOverlayWrapper}
                    text={t("project.fault_events.table.live.tooltips.closable")}
                  />
                }
              >
                <Button
                  variant="tertiary"
                  slim
                  type="button"
                  disabled={onCloseEvent === undefined}
                  onClick={onCloseEvent}
                  leftIcon={<InboxArchiveLine color={colors.carbon800} size="s" />}
                >
                  {t("project.fault_event.chart.evolution.title.bagde.closable")}
                </Button>
              </TooltipsV2>
            ) : null)}
        </div>
        <div style={{ display: "flex", marginLeft: "auto" }}>
          <ExportWidgetButton
            fileName={generateExportFileName({
              filename: t("diagnosis.overview.card.title"),
              projectName: project.name,
              extension: "png",
            })}
          />
          <FullViewportTrigger style={{ marginLeft: spacings.m }} />
        </div>
      </div>
    ),
    [graphOptionSelector, event, project, detectionTag, onCloseEvent],
  );

  if (displayFilterRemovesAllActiveAnalyses) {
    return (
      <ChartCard
        title={titleChart}
        subtitle={subtitleC}
        className={cx(
          failureEvolutionChart,
          FailureEvolutionChartCard,
          FullViewportButtonMarker,
          exportWidgetTarget,
        )}
      >
        <DisplayFiltersBlankState projectId={project.id} />
      </ChartCard>
    );
  }

  return (
    <DiagnosisHighcharts
      title={titleChart}
      subtitle={subtitleC}
      eventScope={eventScope}
      event={event}
      project={project}
      quantities={quantities}
      setups={setups}
      source={source}
      activeAnalyses={activeAnalyses}
      indexedFailureMetadata={indexedFailureMetadata}
      indexedFailureValues={indexedFailureValues}
      indexedManualFailureValues={indexedManualFailureValues}
    />
  );
};

export default FailureEvolution;

// #region CSS
const failureEvolutionChart = css`
  row-gap: ${spacings.m};
`;

const styledOverlayWrapper = css`
  max-width: 350px;
`;

const title = css`
  display: flex;
  column-gap: 40px;
  width: 100%;
  font: ${fonts.h4};
`;

const FailureEvolutionChartCard = css`
  :not(.${FullViewportButtonMarker}) {
    max-height: 80vh;
  }
`;
const centerIcon = css`
  > svg {
    display: block;
  }
`;
const flexSpacingXs = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;

const leftOptionsContainer = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;
// #endregion CSS
